import React from "react";
import type { ApolloClient } from "@apollo/client";
import { push } from "store/actions/router";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { get, isEmpty, isEqual } from "lodash";
import IconSpinner from "components/IconSpinner";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import { getEmployeeDocumentUploadJobs } from "actions/employerActions";
import AddEmployeeUploadFormToggle from "./AddEmployeeUploadFormToggle";
import MissingEmployerGroup from "./MissingEmployerGroup";
import EmployeeUploadJobTable from "./EmployeeUploadJobTable";
import type { RootState } from "types/state";

import "./Employees.scss";

const actions = [employerConstants.GET_EMPLOYER_GROUPS];
const isFetchingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: isFetchingSelector(state),
    groups: state.employer.groups,
    employeeUploadJobs: isEmpty(get(state, "employer.employeeUploadJobs", [])),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  getEmployeeDocumentUploadJobs,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

class UploadEmployees extends React.Component<Props> {
  componentDidMount() {
    window.analytics.page("Employer Employee Upload Page");
    // Pre-existing bug preserved: the action creator expects
    // (client, companyId) but the original only passes `client`.
    // @ts-expect-error — pre-existing missing companyId arg (see action signature)
    void this.props.getEmployeeDocumentUploadJobs(
      this.props.client as ApolloClient<object>
    );
  }

  shouldComponentUpdate(nextProps: Props) {
    // Required so that when we upload an employee doc for the first time the `employeeUploadJobs` prop changes which
    // causes the entire component to render. We want to prevent that so it shows the correct "uploading" component
    // eslint-disable-next-line no-unused-vars
    const { employeeUploadJobs: ignore, ...nextUpcomingProps } = nextProps;
    // eslint-disable-next-line no-unused-vars
    const { employeeUploadJobs: ignore2, ...currentProps } = this.props;
    return !isEqual(nextUpcomingProps, currentProps);
  }

  render() {
    if (this.props.isFetching) {
      return <IconSpinner centered />;
    } else if (isEmpty(this.props.groups)) {
      return <MissingEmployerGroup />;
    } else if (this.props.employeeUploadJobs) {
      return (
        <>
          <AddEmployeeUploadFormToggle
            onUploadSuccess={() => {
              this.props.push("/dashboard/users/employees");
            }}
          />
        </>
      );
    }

    return (
      <div id="account employees-page">
        <EmployeeUploadJobTable />
      </div>
    );
  }
}

export default connector(withApollo<Props>(UploadEmployees));
